import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { Button, createBemBlockBuilder, Loading } from '@ebsco-ui/ebsco-ui';

import {
  getErrorReason,
  Modal,
  ModalNotificationType,
  RecordSummaryModalHeader,
  RenewNotification,
} from '@app/components';
import { useAppContext, useRecordSummaryContext } from '@app/contexts';
import { QUERY_KEYS, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { getCheckoutsStatusMessage, getFormattedDate } from '@app/utils';
import {
  itemStatusPanelButtonMessages,
  sharedMessages,
} from '@app/translations';
import { NOTIFICATION_STATUS } from '@app/constants';

import { useRenewMutation } from './useRenewMutation';

import '../Modal.scss';

interface RenewModalProps {
  isOpen: boolean;
  onModalClose: () => void;
}

const cnBem = createBemBlockBuilder(['modal']);

export const RenewModal: FC<RenewModalProps> = ({ isOpen, onModalClose }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ModalNotificationType | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const intl = useIntl();
  const { $t } = intl;
  const { locale } = useAppContext();
  const renewMutation = useRenewMutation();
  const { instance, itemStatusData } = useRecordSummaryContext();
  const { selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption } =
    useHoldsAndCheckoutsPageConfig();

  const modalTitle = selectConfiguredHoldsAndCheckoutsOption(
    $t({
      id: 'renewModal.renewLoan',
      defaultMessage: 'Renew loan',
    }),
    $t({
      id: 'renewModal.renewCheckout',
      defaultMessage: 'Renew checkout',
    })
  );

  useEffect(() => {
    const queryStatus = renewMutation.status;

    if (
      queryStatus === NOTIFICATION_STATUS.error ||
      queryStatus === NOTIFICATION_STATUS.success
    ) {
      setStatus(queryStatus);
    }

    if (queryStatus === NOTIFICATION_STATUS.success) {
      const date = getFormattedDate({
        date: renewMutation.data.dueDate,
        locale,
      });

      setStatusMessage(getCheckoutsStatusMessage({ date, intl }));
    }
  }, [instance.id, locale, renewMutation.data, renewMutation.status, intl]);

  const handleCloseButtonClick = () => {
    if (status === NOTIFICATION_STATUS.success) {
      queryClient.refetchQueries([QUERY_KEYS.holdsAndCheckoutsPage], {
        type: 'active',
      });
      queryClient.refetchQueries([QUERY_KEYS.userItemStatuses], {
        type: 'active',
      });
    }

    onModalClose();
  };

  const getModalFooter = () => {
    return (
      <>
        <Button
          data-auto="modal-footer-close"
          styleType="text"
          onClick={handleCloseButtonClick}
        >
          {$t(status ? sharedMessages.close : sharedMessages.cancel)}
        </Button>
        {!status && (
          <Button
            title={$t(itemStatusPanelButtonMessages.renew)}
            styleType="default"
            onClick={() => {
              renewMutation.mutate({
                itemId: itemStatusData?.itemId as string,
              });
            }}
          >
            {$t(itemStatusPanelButtonMessages.renew)}
          </Button>
        )}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={modalTitle}
      className={cnBem()}
      onClose={onModalClose}
      onAfterClose={() => setStatus(null)}
    >
      <div
        className={cn({
          [cnBem('__disabledScroll')]: renewMutation.isLoading,
        })}
      >
        <RecordSummaryModalHeader
          headerTitle={modalTitle}
          onModalClose={onModalClose}
        />
        {status && (
          <Modal.Body>
            <RenewNotification
              type={status}
              statusMessage={statusMessage}
              errorMessage={getErrorReason(renewMutation)}
            />
          </Modal.Body>
        )}
        <Modal.Footer>{getModalFooter()}</Modal.Footer>
        {renewMutation.isLoading && (
          <Loading
            loadingMessage={selectConfiguredHoldsAndCheckoutsOption(
              $t({
                id: 'renewModal.renewingLoan',
                defaultMessage: 'Renewing loan...',
              }),
              $t({
                id: 'renewModal.renewingCheckout',
                defaultMessage: 'Renewing checkout...',
              })
            )}
          />
        )}
      </div>
    </Modal>
  );
};
